import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

@Component({
    templateUrl: 'audit-log-item.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class IntegrationAuditLogItemPage implements OnInit {

  guestReservationSyncRun: any;
  guestReservationAddresses: string;
  formattedSyncData: SafeHtml;
  formattedFailureInfo: SafeHtml;
  loaded = false;
  syncDataCollapsed = false;
  failureDataCollapsed = false;
  syncDataFullScreen = false;
  failureDataFullScreen = false;
  syncDataSearchVisible = false;
  failureDataSearchVisible = false;
  syncDataSearch = '';
  failureDataSearch = '';
  syncDataLineCount = 0;
  failureDataLineCount = 0;
  hasFailureDataFlag = false;

  constructor(
    private navCtrl: CustomNavController,
    private guestReservations: GuestReservations,
    public windowService: WindowService,
    private sanitizer: DomSanitizer,
    private util: Util
  ) {}

  async ngOnInit() {
    this.guestReservationSyncRun = this.navCtrl.getParam('guestReservationSyncRun');
    this.guestReservationSyncRun = await this.guestReservations.getAuditLog(this.guestReservationSyncRun.id);

    if(!this.guestReservationSyncRun) {
      return this.navCtrl.navigateRoot('audit-logs');
    }

    try {
      const addresses =
        this.guestReservationSyncRun.data?.sync_results?.units?.map((unit) =>
          this.getPropertyName(unit?.adapted_address)
        );

      this.guestReservationAddresses = addresses.join(', ');
    } catch (_error) {
      this.guestReservationAddresses = '';
    }

    const rawSyncData = JSON.stringify(this.guestReservationSyncRun.data, null, 2);
    const rawFailureData = JSON.stringify(this.guestReservationSyncRun.failure_info, null, 2);
    this.syncDataLineCount = rawSyncData?.split('\n').length || 0;
    this.failureDataLineCount = rawFailureData?.split('\n').length || 0;

    this.formattedSyncData = this.formatJson(this.guestReservationSyncRun.data);
    this.formattedFailureInfo = this.formatJson(this.guestReservationSyncRun.failure_info);
    this.hasFailureDataFlag = this.checkHasFailureData();
    this.loaded = true;
  }

  goBack(): void {
    this.navCtrl.navigateBack('audit-logs');
  }

  getPropertyName(address) {
    if (address?.address_name) {
      return address.address_name;
    }
    if (address?.address2 && address?.address2 !== '') {
      return `${address?.address1  }, ${  address?.address2}`;
    }
    return address?.address1;
  }

  private checkHasFailureData(): boolean {
    const failureInfo = this.guestReservationSyncRun?.failure_info;
    return failureInfo !== null && failureInfo !== undefined && Object.keys(failureInfo).length > 0;
  }

  private formatJson(data: any, searchTerm?: string): SafeHtml {
    if (!data) {
      return this.sanitizer.bypassSecurityTrustHtml('<span class="json-null">null</span>');
    }

    const jsonString = JSON.stringify(data, null, 2);
    const highlighted = this.syntaxHighlight(jsonString, searchTerm);
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  private syntaxHighlight(json: string, searchTerm?: string): string {
    const escaped = json
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    let highlighted = escaped.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?|[{}\[\],:])/g,
      (match) => {
        let cls = 'json-number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'json-key';
            return `<span class="${cls}">${match.slice(0, -1)}</span><span class="json-colon">:</span>`;
          } else {
            cls = 'json-string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean';
        } else if (/null/.test(match)) {
          cls = 'json-null';
        } else if (/[{}\[\]]/.test(match)) {
          cls = 'json-bracket';
        } else if (/[:,]/.test(match)) {
          cls = match === ':' ? 'json-colon' : 'json-comma';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );

    if (searchTerm && searchTerm.trim()) {
      const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const searchRegex = new RegExp(`(${escapedSearch})`, 'gi');
      highlighted = highlighted.replace(searchRegex, '<mark class="json-search-highlight">$1</mark>');
    }

    return highlighted;
  }

  toggleCollapse(type: 'sync' | 'failure'): void {
    if (type === 'sync') {
      this.syncDataCollapsed = !this.syncDataCollapsed;
    } else {
      this.failureDataCollapsed = !this.failureDataCollapsed;
    }
  }

  toggleFullScreen(type: 'sync' | 'failure'): void {
    if (type === 'sync') {
      this.syncDataFullScreen = !this.syncDataFullScreen;
    } else {
      this.failureDataFullScreen = !this.failureDataFullScreen;
    }
  }

  toggleSearch(type: 'sync' | 'failure'): void {
    if (type === 'sync') {
      this.syncDataSearchVisible = !this.syncDataSearchVisible;
      if (!this.syncDataSearchVisible) {
        this.syncDataSearch = '';
        this.formattedSyncData = this.formatJson(this.guestReservationSyncRun.data);
      }
    } else {
      this.failureDataSearchVisible = !this.failureDataSearchVisible;
      if (!this.failureDataSearchVisible) {
        this.failureDataSearch = '';
        this.formattedFailureInfo = this.formatJson(this.guestReservationSyncRun.failure_info);
      }
    }
  }

  onSearchInput(event: Event, type: 'sync' | 'failure'): void {
    const value = (event.target as HTMLInputElement).value;
    if (type === 'sync') {
      this.syncDataSearch = value;
      this.formattedSyncData = this.formatJson(this.guestReservationSyncRun.data, this.syncDataSearch);
    } else {
      this.failureDataSearch = value;
      this.formattedFailureInfo = this.formatJson(this.guestReservationSyncRun.failure_info, this.failureDataSearch);
    }
  }

  async copyToClipboard(type: 'sync' | 'failure'): Promise<void> {
    const data = type === 'sync'
      ? this.guestReservationSyncRun.data
      : this.guestReservationSyncRun.failure_info;

    const jsonString = JSON.stringify(data, null, 2);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(jsonString);
      this.util.showSuccess('Copied to clipboard');
    }
  }

  downloadJson(type: 'sync' | 'failure'): void {
    const data = type === 'sync'
      ? this.guestReservationSyncRun.data
      : this.guestReservationSyncRun.failure_info;

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `audit-log-${type}-${this.guestReservationSyncRun.id}-${timestamp}.json`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    this.util.showSuccess('Download started');
  }

}
